import { isSsoEnabled, ssoReturnPath, type SsoContext } from './ssoSession';
import type { TerminalLogoutCause } from './terminalLogoutCoordinator';

type RecoveryStorage = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>;

interface TerminalPassiveRecoveryMarker {
  version: 1;
  claimedAt: number;
}

const TERMINAL_PASSIVE_RECOVERY_PREFIX = 'ra2n_sso_terminal_hydration_recovery';
export const TERMINAL_PASSIVE_RECOVERY_TTL_MS = 300_000;

export const assignTerminalPassiveRecoveryLocation = (destination: string): void => {
  window.location.assign(destination);
};

const markerKey = (context: SsoContext): string => {
  // Reuse the SSO context assertion before deriving a browser-storage key.
  ssoReturnPath(context);
  return `${TERMINAL_PASSIVE_RECOVERY_PREFIX}:${context.site}:${context.entryType}`;
};

const parseMarker = (value: string): TerminalPassiveRecoveryMarker | undefined => {
  try {
    const parsed: unknown = JSON.parse(value);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      !('version' in parsed) ||
      parsed.version !== 1 ||
      !('claimedAt' in parsed) ||
      !Number.isFinite(parsed.claimedAt)
    ) {
      return undefined;
    }

    return parsed as TerminalPassiveRecoveryMarker;
  } catch {
    return undefined;
  }
};

export const clearTerminalPassiveRecovery = (
  context: SsoContext,
  storage: Pick<Storage, 'getItem' | 'removeItem'>,
): boolean => {
  try {
    const key = markerKey(context);
    const existed = storage.getItem(key) !== null;
    storage.removeItem(key);
    return existed;
  } catch {
    return false;
  }
};

const claimTerminalPassiveRecovery = ({
  context,
  storage,
  now,
}: {
  context: SsoContext;
  storage: RecoveryStorage;
  now: number;
}): boolean => {
  const key = markerKey(context);
  const existingValue = storage.getItem(key);

  if (existingValue !== null) {
    const existing = parseMarker(existingValue);
    if (existing === undefined || existing.claimedAt > now) {
      storage.removeItem(key);
      return false;
    }
    if (now - existing.claimedAt < TERMINAL_PASSIVE_RECOVERY_TTL_MS) {
      return false;
    }
    storage.removeItem(key);
  }

  if (!Number.isFinite(now)) {
    return false;
  }

  storage.setItem(key, JSON.stringify({
    version: 1,
    claimedAt: now,
  } satisfies TerminalPassiveRecoveryMarker));
  return true;
};

/**
 * Selects one bounded passive SSO retry for a stale session discovered while
 * hydrating the exact portal root. All other terminal failures retain the
 * stable checked-login behavior owned by the base application.
 */
export const selectTerminalPassiveRecoveryDestination = ({
  enabled,
  cause,
  recoveryEligible,
  context,
  checkedLoginDestination,
  expectedCheckedLoginDestination,
  passiveSsoEnabled,
  storage,
  buildDestination,
  now = Date.now(),
}: {
  enabled: boolean;
  cause: TerminalLogoutCause;
  recoveryEligible: boolean;
  context: SsoContext;
  checkedLoginDestination: string;
  expectedCheckedLoginDestination: string;
  passiveSsoEnabled: unknown;
  storage: RecoveryStorage;
  buildDestination: () => string;
  now?: number;
}): string | undefined => {
  if (
    !enabled ||
    cause !== 'session_hydration' ||
    !recoveryEligible ||
    checkedLoginDestination !== expectedCheckedLoginDestination ||
    !isSsoEnabled(passiveSsoEnabled)
  ) {
    return undefined;
  }

  let destination: string;
  try {
    destination = buildDestination();
  } catch {
    return undefined;
  }

  try {
    return claimTerminalPassiveRecovery({
      context,
      storage,
      now,
    }) ? destination : undefined;
  } catch {
    return undefined;
  }
};
